import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../components/PageHeader';
import './Tutorials.css';
import { useBrandIdentity } from '../utils/brandIdentity';

function Tutorials({ onNavigate, showTour = true }) {
  const { appName } = useBrandIdentity();
  return (
    <div className="tutorials-page">
      <Helmet>
        <title>{`Video Tutorials - ${appName}`}</title>
        <meta name="description" content={`Watch video tutorials to learn how to use ${appName} for strategy risk assessment, CSV uploads, and strategy results.`} />
        <meta property="og:title" content={`Video Tutorials - ${appName}`} />
        <meta property="og:description" content={`Watch video tutorials to learn how to use ${appName} for strategy risk assessment, CSV uploads, and strategy results.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://risklo.io/tutorials" />
      </Helmet>
      
      <PageHeader onNavigate={onNavigate} />
      
      <div className="tutorials-content">
        <h1>Video Tutorials</h1>
        <p className="tutorials-intro">
          Learn how to use {appName} with our step-by-step video guides. Watch these tutorials to get started quickly and make the most of all features.
        </p>
        
        {showTour ? (
        <div className="interactive-tour-section">
          <button 
            className="interactive-tour-btn"
            onClick={() => {
              onNavigate('home');
              // Trigger the guided tour after a short delay to allow page navigation
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('showGuidedTour'));
              }, 300);
            }}
          >
            <span className="tour-icon">🎯</span>
            <span className="tour-text">
              <strong>Take the Interactive Tour</strong>
              <small>Guided walkthrough of {appName} features</small>
            </span>
          </button>
        </div>
        ) : null}

        <div className="tutorials-grid">
          {/* Tutorial 1: Getting Started / Guided Tour */}
          <div className="tutorial-card">
            <div className="tutorial-video">
              <iframe
                width="100%"
                height="315"
                src="https://www.youtube.com/embed/3rCO4Rw1pe4"
                title={`${appName} Getting Started Tutorial`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="tutorial-info">
              <h3>Getting Started with {appName}</h3>
              <p>Learn the basics of {appName}, including how to navigate the dashboard, analyze strategies, and understand risk metrics.</p>
            </div>
          </div>

        {/* Tutorial 2: CSV Export */}
          <div className="tutorial-card">
            <div className="tutorial-video">
              <iframe
                width="100%"
                height="315"
                src="https://www.youtube.com/embed/OvZUKQI12_E"
                title="How to Export NinjaTrader CSVs"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="tutorial-info">
              <h3>How to Export NinjaTrader CSVs</h3>
              <p>Learn how to export your account and strategy data from NinjaTrader and upload them to {appName} for bulk risk analysis.</p>
            </div>
          </div>

          <div className="tutorial-card">
            <div className="tutorial-video">
              <iframe
                width="100%"
                height="315"
                src="https://www.youtube.com/embed/auAnbVX8F6o"
                title={`Create a ${appName} account and sign in`}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="tutorial-info">
              <h3>Create an account &amp; log in</h3>
              <p>
                Step-by-step: how to create your {appName} account and sign in (email/password, Google, and other options
                where available).
              </p>
            </div>
          </div>
        </div>

        <div className="tutorials-help">
          <h2>Need More Help?</h2>
          <p>If you have questions or need additional guidance, check out our <button onClick={() => onNavigate('faq')} className="inline-link">FAQ page</button> or <a href="mailto:info@cultivatedynamics.com">contact our support team</a>.</p>
        </div>
      </div>
    </div>
  );
}

export default Tutorials;
